import { Button, CheckboxCard, CheckboxWrapper } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useState } from 'react';

import { useFlow } from '../../../../providers';
import { Search as SearchUI } from '../../../Search/Search';
import { Option } from '../../../Search/SearchResults';

export interface ListItem {
  name: string;
  id: string;
  thumbnail?: {
    filename: string;
    alt: string;
  };
  selected?: boolean;
}

interface Props {
  title: string;
  cta: string;
  featuredItems: ListItem[];
  onChange: (val: string) => void;
  query: string;
  suggestions: Option[];
  placeholder?: string;
  onSubmit?: (input: Record<string, ListItem>) => void;
  values?: Record<string, ListItem>;
  reverse?: boolean;
}

const List = ({
  items,
  selected,
  setSelected,
}: {
  setSelected: React.Dispatch<
    React.SetStateAction<Record<string, ListItem | undefined>>
  >;
  selected: Record<string, ListItem | undefined>;
  items: ListItem[];
}) => {
  if (!items) return null;
  const list = items.map((item) => (
    <CheckboxCard
      key={item.id}
      checked={Boolean(selected[item.id]?.selected)}
      id={item.id}
      label={item.name}
      name={item.id}
      onChange={() => {
        setSelected((prev) => {
          return {
            ...prev,
            [item.id]: {
              ...item,
              selected: Boolean(!prev[item.id]?.selected),
            },
          };
        });
      }}
      thumbnail={item.thumbnail}
      type="checkbox"
      value={item.id}
    />
  ));

  return <>{list}</>;
};

export const Search = ({
  cta,
  featuredItems,
  onChange,
  onSubmit,
  placeholder,
  query,
  reverse,
  suggestions,
  title,
  values,
}: Props) => {
  const { t } = useTranslation('onboarding');
  const [selected, setSelected] = useState<Record<string, ListItem>>(
    values || {},
  );
  const { goNextStep } = useFlow();

  useEffect(() => {
    if (values && Object.keys(selected).length === 0) setSelected(values);
  }, [selected, values]);

  const featuredItemIds = featuredItems.map((item) => item.id);

  const selectedListItems = Object.values(selected).filter(
    (item) => !featuredItemIds.includes(item.id),
  );

  const list = useMemo(() => {
    return [
      ...(reverse ? selectedListItems.reverse() : selectedListItems),
      ...featuredItems,
    ];
  }, [featuredItems, reverse, selectedListItems]);

  const handleNext = () => {
    if (onSubmit) onSubmit(selected);
    goNextStep();
  };

  return (
    <div className="flex flex-col gap-6 px-6 pb-6">
      <div className="flex flex-col gap-3 px-3">
        <h2 className="text-center font-bold md:text-md">{t(title)}</h2>
      </div>
      <div className="flex flex-col gap-3">
        <SearchUI
          onChange={(val) => onChange(val)}
          onSelect={(option) => {
            setSelected((prevState) => ({
              ...prevState,
              [option.id]: {
                ...option,
                name: option.title,
                thumbnail: option.thumbnail
                  ? {
                      filename: option.thumbnail,
                      alt: option.title,
                    }
                  : undefined,
                selected: true,
              },
            }));
          }}
          onSubmit={() => {}}
          placeholder={placeholder}
          query={query}
          suggestions={suggestions}
        />

        <div className="min-h-[180px]">
          <CheckboxWrapper>
            <List items={list} selected={selected} setSelected={setSelected} />
          </CheckboxWrapper>
        </div>
      </div>

      <Button
        className="sticky"
        disabled={!selected}
        fullWidth
        label={t(cta)}
        onClick={handleNext}
        size="lg"
        type="submit"
        variant="primary"
      />
    </div>
  );
};
